import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useStaffAuth } from '@/features/auth/useStaffAuth';
import {
  ActivateSimDialog,
  EditSimDialog,
  NewSimDialog,
} from '@/features/inventory/SimDialogs';
import {
  Pill,
  PRIMARY_BUTTON,
  RegisterScreen,
  SECONDARY_BUTTON,
  formatInstant,
  type RegisterChip,
  type RegisterColumn,
} from '@/features/inventory/registerUi';
import { useSims, type AdminSim, type SimStatus } from '@/features/inventory/useInventory';

/**
 * The SIM register — slice 18d.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO ARTBOARD FOR THIS SCREEN. SAID PLAINLY RATHER THAN APPROXIMATED.
 * ─────────────────────────────────────────────────────────────────────────────
 * `1u` is titled "devices & SIMs" and draws ONE table, of devices, with a SIM
 * column. No canvas screen draws the SIM register on its own, and none draws an
 * activation dialog or an assignment history. So this screen matches the SHIPPED
 * admin screens — `Tenants` and `Plans` — for chrome, spacing and states, and
 * shares every component with `Devices`. That is a deliberate substitute for a
 * pixel source, not a claim to have matched one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THERE IS NO PER-SIM HISTORY CONTROL, AND ITS ABSENCE IS AN API GAP.
 * ─────────────────────────────────────────────────────────────────────────────
 * `GET /admin/devices/:id/assignments` answers both axes for a DEVICE. There is
 * no `GET /admin/sims/:id/assignments`, so "which devices has this SIM been in"
 * cannot be answered without one request per device in the register and a
 * client-side reverse index — which would be a second, slower, definition of a
 * fact the server already holds. The honest options were a control that lies by
 * omission or no control; this is no control. The device register shows the
 * other direction of the same rows today, and the missing route is a follow-up
 * for the `api/` half.
 *
 * WHAT IS DELIBERATELY SHOWN: `activatedAt`, because a SIM with no number is the
 * one register state that silently produces a tracker that never reports.
 */

const SIM_TONE: Record<SimStatus, string> = {
  in_service: 'border-raad-border text-raad-muted',
  retired: 'border-raad-error/40 text-raad-error',
  lost: 'border-raad-error/40 text-raad-error',
};

export function Sims() {
  const { t, i18n } = useTranslation();
  const { state } = useStaffAuth();
  // Read once: it gates the redirect below AND the queries, so a non-super-admin
  // neither sees the screen nor calls a route that would only 403.
  const isSuperAdmin = state.status === 'authenticated' && state.user.role === 'super_admin';
  const simsQ = useSims(isSuperAdmin);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminSim | null>(null);
  const [activating, setActivating] = useState<AdminSim | null>(null);

  if (state.status === 'authenticated' && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (simsQ.isPending) return <p className="text-sm text-raad-muted">{t('inventory.loading')}</p>;

  const rows = Array.isArray(simsQ.data?.sims) ? simsQ.data.sims : null;
  if (simsQ.isError || rows === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('inventory.sims.error')}
      </p>
    );
  }

  const installed = rows.filter((s) => !s.inStock).length;
  const inStock = rows.filter((s) => s.inStock).length;
  const waiting = rows.filter((s) => s.msisdn === null).length;

  const columns: Array<RegisterColumn<AdminSim>> = [
    {
      key: 'iccid',
      label: t('inventory.sims.iccid'),
      render: (s) => (
        <span className="num font-medium">
          {s.iccid}
          {s.notes ? (
            <span className="mt-0.5 block text-[11.5px] font-normal text-raad-muted">{s.notes}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'msisdn',
      label: t('inventory.sims.msisdn'),
      render: (s) =>
        s.msisdn === null ? (
          <span className="text-raad-warn">{t('inventory.sims.notActivated')}</span>
        ) : (
          <>
            <span className="num">{s.msisdn}</span>
            {s.activatedAt ? (
              <span className="mt-0.5 block text-[11.5px] text-raad-muted">
                {formatInstant(s.activatedAt, i18n.language)}
              </span>
            ) : null}
          </>
        ),
    },
    {
      key: 'carrier',
      label: t('inventory.sims.carrier'),
      render: (s) => (
        <>
          <span>{s.carrier}</span>
          {s.apn ? <span className="mt-0.5 block text-[11.5px] text-raad-muted">{s.apn}</span> : null}
        </>
      ),
    },
    {
      key: 'device',
      label: t('inventory.sims.device'),
      render: (s) =>
        s.deviceImei === null ? (
          <span className="text-raad-muted">{t('inventory.inStock')}</span>
        ) : (
          <span className="num">{s.deviceImei}</span>
        ),
    },
    {
      key: 'status',
      label: t('inventory.status'),
      render: (s) => <Pill label={t(`inventory.simStatus.${s.status}`)} tone={SIM_TONE[s.status]} />,
    },
    {
      key: 'actions',
      label: t('inventory.actions'),
      render: (s) => (
        <span className="flex flex-wrap gap-2">
          <button type="button" className={SECONDARY_BUTTON} onClick={() => setEditing(s)}>
            {t('inventory.edit')}
          </button>
          {/* Activation is one-way, so the control disappears once the number
              exists rather than sitting there greyed out — there is nothing it
              could do a second time, and the server refuses it. */}
          {s.msisdn === null ? (
            <button type="button" className={SECONDARY_BUTTON} onClick={() => setActivating(s)}>
              {t('inventory.sims.activate')}
            </button>
          ) : null}
        </span>
      ),
    },
  ];

  const chips: Array<RegisterChip<AdminSim>> = [
    { key: 'all', label: t('inventory.chip.all'), predicate: () => true },
    { key: 'installed', label: t('inventory.chip.fitted'), predicate: (s) => !s.inStock },
    { key: 'stock', label: t('inventory.chip.stock'), predicate: (s) => s.inStock },
    {
      key: 'waiting',
      label: t('inventory.chip.awaitingNumber'),
      predicate: (s) => s.msisdn === null,
    },
    {
      key: 'attention',
      label: t('inventory.chip.attention'),
      predicate: (s) => s.status !== 'in_service',
    },
  ];

  return (
    <RegisterScreen
      headingId="sims-heading"
      title={t('inventory.sims.title')}
      headline={t('inventory.sims.headline', { installed, stock: inStock, waiting })}
      headerAction={
        <button type="button" className={PRIMARY_BUTTON} onClick={() => setCreating(true)}>
          {t('inventory.sims.new')}
        </button>
      }
      rows={rows}
      columns={columns}
      chips={chips}
      searchLabel={t('inventory.sims.search')}
      matches={(s, needle) =>
        [s.iccid, s.msisdn, s.carrier, s.apn, s.deviceImei, s.notes]
          .filter((v): v is string => typeof v === 'string')
          .some((v) => v.toLowerCase().includes(needle))
      }
      emptyText={t('inventory.sims.empty')}
      noMatchText={(q) => t('inventory.sims.searchNone', { q })}
    >
      {creating ? <NewSimDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <EditSimDialog sim={editing} onClose={() => setEditing(null)} /> : null}
      {activating ? (
        <ActivateSimDialog sim={activating} onClose={() => setActivating(null)} />
      ) : null}
    </RegisterScreen>
  );
}

export default Sims;
